import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Optional,
  computed,
  signal
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { NavigateActionBlock } from '@core/services/ai/ai-assistant.service';
import { defaultIconFor } from '../entity-deeplink.helper';
import { normalizeQueryParams, safeInAppRoute } from '../safe-route.helper';

/**
 * Renders a `navigate-action` widget — a single deep-link CTA the
 * assistant uses to hand the user off to an in-app create form (lease,
 * tenant, repair, …). External URLs are rejected so a prompt-injected
 * route cannot phish the user out of the app.
 */
@Component({
  selector: 'app-ai-navigate-action-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatIconModule, RouterLink],
  template: `
    <div class="nav-action" [attr.data-tone]="block.tone || 'neutral'">
      <mat-icon class="nav-action__icon">{{ resolvedIcon() }}</mat-icon>
      <div class="nav-action__body">
        @if (resolvedDescription(); as desc) {
          <div class="nav-action__desc">{{ desc }}</div>
        }
        <a
          class="nav-action__cta"
          mat-flat-button
          color="primary"
          [routerLink]="safeRoute()"
          [queryParams]="normalizedQueryParams()">
          {{ resolvedLabel() }}
          <mat-icon class="nav-action__cta-arrow">arrow_forward</mat-icon>
        </a>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .nav-action {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 14px;
        border-radius: 12px;
        border: 1px solid rgba(99, 102, 241, 0.25);
        border-left: 4px solid #6366f1;
        background: linear-gradient(
          135deg,
          rgba(99, 102, 241, 0.06),
          rgba(99, 102, 241, 0.02)
        );
      }
      .nav-action[data-tone='positive'] {
        border-color: rgba(16, 185, 129, 0.3);
        border-left-color: #10b981;
        background: linear-gradient(
          135deg,
          rgba(16, 185, 129, 0.08),
          rgba(16, 185, 129, 0.02)
        );
      }
      .nav-action[data-tone='warning'] {
        border-color: rgba(245, 158, 11, 0.3);
        border-left-color: #f59e0b;
        background: linear-gradient(
          135deg,
          rgba(245, 158, 11, 0.08),
          rgba(245, 158, 11, 0.02)
        );
      }
      .nav-action[data-tone='danger'] {
        border-color: rgba(239, 68, 68, 0.3);
        border-left-color: #ef4444;
        background: linear-gradient(
          135deg,
          rgba(239, 68, 68, 0.08),
          rgba(239, 68, 68, 0.02)
        );
      }
      .nav-action__icon {
        flex-shrink: 0;
        font-size: 22px;
        width: 22px;
        height: 22px;
        color: #6366f1;
        margin-top: 2px;
      }
      .nav-action[data-tone='positive'] .nav-action__icon {
        color: #059669;
      }
      .nav-action[data-tone='warning'] .nav-action__icon {
        color: #d97706;
      }
      .nav-action[data-tone='danger'] .nav-action__icon {
        color: #dc2626;
      }
      .nav-action__body {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
      .nav-action__desc {
        font-size: 13px;
        color: rgba(15, 23, 42, 0.75);
        line-height: 1.45;
        overflow-wrap: anywhere;
      }
      .nav-action__cta {
        align-self: flex-start;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .nav-action__cta-arrow {
        font-size: 16px;
        width: 16px;
        height: 16px;
      }
    `
  ]
})
export class AiNavigateActionBlockComponent {
  private readonly _block = signal<NavigateActionBlock | null>(null);

  /**
   * Constructor injection (over `inject()`) so the component can also be
   * instantiated with `new` in pure unit tests without spinning up
   * TestBed. When `null`, the i18n keys gracefully fall back to the
   * server-provided English label/description.
   */
  constructor(
    @Optional() private readonly translate: TranslateService | null = null
  ) {}

  @Input({ required: true }) set block(value: NavigateActionBlock) {
    this._block.set(value);
  }
  get block(): NavigateActionBlock {
    return this._block()!;
  }

  /**
   * When `labelKey` is set and resolves to something other than the key
   * itself (ngx-translate convention for missing keys), use the
   * translation. Otherwise fall back to the English `label` from the
   * server. Same logic for description.
   */
  readonly resolvedLabel = computed(() => {
    const b = this._block();
    if (!b) return '';
    return this.resolveKey(b.labelKey, b.label);
  });

  readonly resolvedDescription = computed<string | null>(() => {
    const b = this._block();
    if (!b) return null;
    if (!b.description && !b.descriptionKey) return null;
    return this.resolveKey(b.descriptionKey, b.description ?? '') || null;
  });

  private resolveKey(key: string | undefined, fallback: string): string {
    if (!key) return fallback;
    if (!this.translate) return fallback;
    const translated = this.translate.instant(key);
    if (typeof translated !== 'string' || translated === key) return fallback;
    return translated;
  }

  /**
   * Reject anything that isn't an in-app path. A prompt-injected
   * `https://attacker.example` value would otherwise turn into an
   * external link rendered as a styled CTA — too easy to phish.
   * Delegates to the shared guard so alert/entity-list stay in sync.
   */
  readonly safeRoute = computed(() => safeInAppRoute(this._block()?.route));

  readonly normalizedQueryParams = computed(() =>
    normalizeQueryParams(this._block()?.queryParams)
  );

  resolvedIcon(): string {
    const b = this._block();
    if (!b) return 'open_in_new';
    if (b.icon) return b.icon;
    if (b.entityType) return defaultIconFor(b.entityType);
    return 'open_in_new';
  }
}
